/**
 * A policy's periodic reviews: "Reviewed, no changes", every time it happened.
 *
 * The record has always been written — who reviewed, when, their notes, and
 * the next review date it set — and nothing in the product ever read it. A
 * hospital could be asked to show that a policy had been reviewed on its cycle
 * and had no way to produce the evidence its own system had recorded.
 *
 * Read straight from Firestore, as versions are, and governed by the same
 * rule: any member of the organization. This is the other half of a policy's
 * history, not a second source of truth — nothing here is recomputed, and a
 * no-change review still creates no version, because no version of the
 * document was made.
 */

import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, orderBy, query } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { toIsoDate } from './usePolicy';

const db = getFirestore(app);

export interface PolicyReview {
  id: string;
  /** ISO date-time. The moment the review was recorded. */
  reviewedAt: string | null;
  reviewedByUid: string | null;
  /** Whatever the record kept of who they are. Never invented. */
  reviewerName: string | null;
  reviewerEmail: string | null;
  /** `no_changes` is the only kind written today. */
  reviewType: string;
  notes: string | null;
  /** The date this review set for the next one. */
  nextReviewDate: string | null;
  /** What the clock said before this review, when the record kept it. */
  previousReviewDate: string | null;
}

/** In words, for a row. */
export function reviewOutcome(r: PolicyReview): string {
  return r.reviewType === 'no_changes' ? 'Reviewed — no changes' : 'Reviewed';
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/** A full timestamp, because "when" is the point of the record. */
export function toMomentString(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const d = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

export function toReview(id: string, d: Record<string, unknown>): PolicyReview {
  return {
    id,
    reviewedAt: toMomentString(d.reviewedAt) ?? toMomentString(d.createdAt),
    reviewedByUid: str(d.reviewedBy),
    reviewerName: str(d.reviewerDisplayName),
    reviewerEmail: str(d.reviewerEmail),
    reviewType: str(d.reviewType) ?? 'no_changes',
    notes: str(d.notes),
    nextReviewDate: toIsoDate(d.nextReviewDate),
    previousReviewDate: toIsoDate(d.previousReviewDate),
  };
}

export function usePolicyReviews(orgId: string | null, policyId: string | undefined) {
  return useQuery({
    queryKey: ['policy-reviews', orgId, policyId],
    enabled: Boolean(orgId && policyId),
    queryFn: async (): Promise<PolicyReview[]> => {
      const snap = await getDocs(query(
        collection(db, 'orgs', orgId!, 'policies', policyId!, 'reviewHistory'),
        orderBy('reviewedAt', 'desc'),
      ));
      return snap.docs.map((d) => toReview(d.id, d.data()));
    },
  });
}
